import { AnswerTag } from '@teuteuf-games/answer-tag';
import { InputForm } from '@teuteuf-games/input-form';
import { getCompassDirection, getDistance } from 'geolib';
import { DateTime } from 'luxon';
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getGuessButtonProps } from '../../components/buttons/GuessButton';
import { FlagGrid } from '../../components/FlagGrid';
import { GuessList } from '../../components/GuessList';
import { RoundEndedButtons } from '../../components/RoundEndedButtons';
import {
  MAX_ATTEMPTS,
  TILE_COUNT,
  TILES_REVEALED_AT_START,
} from '../../constants';
import { useConfettiThrower } from '../../hooks/useConfettiThrower';
import { useDailySeed } from '../../hooks/useDailySeed';
import { useGuessHistory } from '../../hooks/useGuessHistory';
import i18n from '../../i18n';
import { useTodaysCountry } from '../../providers/TodaysCountryProvider';
import { setData } from '../../utils/account';
import { getCountryName } from '../../utils/countryName';
import { shuffleWithSeed } from '../../utils/shuffleWithSeed';
import { useStats } from '../../utils/stats';

const TILE_INDICES = Array.from({ length: TILE_COUNT }, (_, i) => i);
const PRANK_FLAG_URL = '/images/flags/prank1.svg';

const STREAK_KEEPER_DATES = new Set(['2026-04-01']);

export function MainGameRoute() {
  const { t } = useTranslation();
  const {
    todaysCountry,
    countryList,
    rounds,
    saveCompletedRound,
    isArchive,
    isPrankMode,
    markPrankRevealed,
    setShowPrankModal,
  } = useTodaysCountry();
  const [flippedArray, setFlippedArray] = useState(Array(6).fill(false));
  const baseDayString = useDailySeed(isArchive);
  const prankSeed = isPrankMode ? `prank-${baseDayString}` : undefined;
  const dayString = prankSeed ?? baseDayString;
  const [end, setEnd] = useState(false);
  const [, setStats] = useStats();
  const [guessHistory, addGuess] = useGuessHistory(isArchive, prankSeed);
  const guesses = useMemo(
    () => guessHistory[dayString] || [],
    [guessHistory, dayString],
  );
  const [randomOrder, setRandomOrder] = useState(() =>
    shuffleWithSeed(TILE_INDICES, dayString).slice(guesses.length),
  );
  const countyInputRef = useRef<HTMLInputElement>(null);
  const [currentGuess, setCurrentGuess] = useState('');

  const trueCountry = useMemo(
    () => getCountryName(i18n.language, todaysCountry),
    [todaysCountry],
  );

  const allCountryNames = useMemo(
    () => countryList.map((c) => c.name),
    [countryList],
  );

  const revealRandomTile = useCallback(() => {
    const [tile] = randomOrder;
    setRandomOrder(randomOrder.slice(1));
    setFlippedArray((currArray) => {
      const newFlipped = [...currArray];
      newFlipped[tile] = true;
      return newFlipped;
    });
    return tile;
  }, [setFlippedArray, randomOrder]);

  const getRemainingTiles = useCallback(() => {
    const remainingTiles = [];
    const usedTiles = guesses.map((guess) => guess.tile);
    for (const i of TILE_INDICES) {
      if (!usedTiles.includes(i)) {
        remainingTiles.push(i);
      }
    }
    return remainingTiles;
  }, [guesses]);

  const revealTiles = useCallback(() => {
    setFlippedArray((currFlipped) => {
      const newFlipped = [...currFlipped];

      for (const guess of guesses) {
        newFlipped[guess.tile] = true;
      }
      return newFlipped;
    });
  }, [setFlippedArray, guesses]);

  const throwConfetti = useConfettiThrower();

  useEffect(() => {
    if (end) return;
    revealTiles();
    getRemainingTiles();
    const lastGuess = guesses[guesses.length - 1];
    if (guesses.length >= MAX_ATTEMPTS || lastGuess?.distance === 0) {
      if (!isPrankMode) {
        saveCompletedRound('Flag');
      }
      setEnd(true);
      setFlippedArray(Array(6).fill(true));
      if (lastGuess?.distance === 0) {
        // Reveal toast disabled for now — it overlays the top of the screen.
        // toast(`🎉 ${trueCountry} 🎉`, { delay: 2000, autoClose: 3000 });
        setTimeout(throwConfetti, 2000);
      } else {
        // toast(`🤔 ${trueCountry} 🤔`, { delay: 2000, autoClose: 3000 });
      }
    }
  }, [
    guesses,
    trueCountry,
    getRemainingTiles,
    revealTiles,
    throwConfetti,
    end,
    saveCompletedRound,
    isPrankMode,
  ]);

  useEffect(() => {
    if (end && isPrankMode) {
      markPrankRevealed();
      const timer = setTimeout(() => setShowPrankModal(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [end, isPrankMode, markPrankRevealed, setShowPrankModal]);

  // reveal the first tile when the game starts
  useEffect(() => {
    if (randomOrder.length < 6 || !TILES_REVEALED_AT_START) return;

    setFlippedArray((prev) => {
      const newFlippedArray = [...prev];
      for (let i = 0; i < TILES_REVEALED_AT_START; i++) {
        newFlippedArray[randomOrder[i]] = true;
      }
      return newFlippedArray;
    });

    setRandomOrder((randomOrder) => randomOrder.slice(1));
  }, [setFlippedArray, setRandomOrder, randomOrder]);

  const onGuess = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!countryList) return;

      const guessedCountry = countryList.find(
        (c) => c.name.toUpperCase() === currentGuess.toUpperCase(),
      );

      if (guessedCountry == null) {
        toast.error(t('unknown-country'), {
          autoClose: 3000,
        });
        setCurrentGuess('');
        return;
      }

      if (
        guesses.findIndex(
          (g) => g.name.toUpperCase() === currentGuess.toUpperCase(),
        ) !== -1
      ) {
        setCurrentGuess('');
        return toast.error(t('main.already-guessed', { currentGuess }), {
          autoClose: 3000,
        });
      }

      const tileNum = revealRandomTile();
      const distance = getDistance(guessedCountry, todaysCountry);
      const newGuess = {
        name: currentGuess.toUpperCase(),
        distance,
        direction: getCompassDirection(guessedCountry, todaysCountry),
        tile: tileNum,
      };
      addGuess(newGuess);
      setCurrentGuess('');
      if (!isArchive && (distance == 0 || guesses.length + 1 >= MAX_ATTEMPTS)) {
        // Update stats
        setStats((prev) => {
          const trueDayString = baseDayString.substring(
            0,
            baseDayString.length - 2,
          );
          const newGuessDistribution = [...prev.guessDistribution];
          let newStreak = prev.currentStreak;
          let sameDay = false;

          // Check streak continuity
          if (prev.previousDate) {
            // prev.previousDate may be of the form yyyy-MM-dd OR 2026-04-01T00:00:00.000+01:00
            const previousDateString =
              prev.previousDate?.toString().split('T')[0] ?? '';
            const previousDate = DateTime.fromFormat(
              previousDateString,
              'yyyy-MM-dd',
            );
            const currentDate = DateTime.fromFormat(
              trueDayString,
              'yyyy-MM-dd',
            );
            if (previousDate.hasSame(currentDate, 'day')) {
              // Already played today (e.g. prank then real game)
              sameDay = true;
            } else if (
              !previousDate.plus({ days: 1 }).hasSame(currentDate, 'day')
            ) {
              // Keep streak alive if the gap day is a streak-keeper date
              const gapIsStreakKeeper = STREAK_KEEPER_DATES.has(trueDayString);
              if (!gapIsStreakKeeper) {
                newStreak = 0;
              }
            }
          } else {
            newStreak = 0;
          }

          const isWin = distance === 0;
          const isStreakKeeperWin = STREAK_KEEPER_DATES.has(trueDayString);

          if (isPrankMode) {
            // Prank always advances streak regardless of win/loss
            newStreak += 1;
            if (isWin) {
              newGuessDistribution[guesses.length - 1] += 1;
            }
          } else if (sameDay) {
            // Real game after prank on same day — don't advance streak
            if (isWin) {
              newGuessDistribution[guesses.length - 1] += 1;
            }
          } else {
            // Normal real game
            if (isWin || isStreakKeeperWin) {
              newGuessDistribution[guesses.length - 1] += 1;
              newStreak += 1;
            }
          }

          const newStats = {
            ...prev,
            played: prev.played + 1,
            winCount: prev.winCount + (isWin || isStreakKeeperWin ? 1 : 0),
            guessDistribution: newGuessDistribution,
            previousDate: DateTime.fromFormat(trueDayString, 'yyyy-MM-dd'),
            currentStreak: newStreak,
            maxStreak: Math.max(newStreak, prev.maxStreak),
          };
          setData('stats', newStats);
          setData('guesses', {
            [dayString]: [...guesses, newGuess],
          });
          return newStats;
        });
      }
    },
    [
      guesses,
      revealRandomTile,
      countryList,
      todaysCountry,
      addGuess,
      currentGuess,
      dayString,
      baseDayString,
      setStats,
      t,
      isArchive,
      isPrankMode,
    ],
  );

  const currentRound = rounds.find((r) => r.name === 'Flag');
  const nextRoundIndex = currentRound ? rounds.indexOf(currentRound) + 1 : 0;

  return (
    <>
      <div className="flex flex-col w-full max-w-lg">
        <FlagGrid
          end={end}
          countryInfo={{ code: todaysCountry.code }}
          flippedArray={flippedArray}
          flagUrl={isPrankMode ? PRANK_FLAG_URL : undefined}
        ></FlagGrid>
        {end && (
          <div className="flex w-full items-center justify-center">
            <AnswerTag
              answerState={
                guesses.length && guesses[guesses.length - 1].distance === 0
                  ? 'right'
                  : 'default'
              }
              text={
                <>
                  {t('main.flag')}:&nbsp;
                  <span className="font-semibold">
                    {trueCountry?.toUpperCase() ?? ''}
                  </span>
                </>
              }
              textSize="text-md"
              padding="py-1 px-2"
              margin="mt-3"
            />
          </div>
        )}

        <p className="my-2 text-sm">
          {TILES_REVEALED_AT_START === 0 &&
            guesses?.length === 0 &&
            t('main.make-a-guess')}
          &nbsp;
        </p>
        {!end && (
          <div className="text-start">
            <InputForm
              handleSubmit={onGuess}
              inputRef={countyInputRef}
              currentGuess={currentGuess}
              setCurrentGuess={setCurrentGuess}
              autosuggestValues={allCountryNames?.sort() ?? []}
              placeholderText={t('main.country')}
              buttonProps={{
                ...getGuessButtonProps(t('guess')),
              }}
            />
          </div>
        )}
        <GuessList
          guesses={guesses}
          maxTryCount={MAX_ATTEMPTS}
          gameEnded={end}
          inputRef={countyInputRef}
        />
      </div>
      {end && (
        <div className="my-5 flex w-full">
          <RoundEndedButtons nextBonusRoundIndex={nextRoundIndex} />
        </div>
      )}
    </>
  );
}
